import {
  type QueryClient,
  queryOptions,
  useQuery,
  useSuspenseQuery,
} from '@tanstack/react-query';
import { z } from 'zod';
import type { ApiResponse } from '@/types/api-types';
import type { QueryOptions } from '@/types/tanstack-query-options';
import type { paths } from '~/generated/smart-form/api-schema';
import {
  categoryDisplaySchema,
  type FormCodeType,
} from '@/consts/backend-i18n-derived';
import { smartFormApi } from '@/services';

// TODO: Wait to move this service to another system; it should not be used in smart form.
const PATH = '/v1/template/queryFormCode' as const;

export { PATH as QUERY_FORM_CODE };

type Response = ApiResponse<paths, typeof PATH>;

const categoryTypeSchema = z.enum(['CATEGORY', 'TYPE', 'SPECIALTY']);

export type CategoryType = z.infer<typeof categoryTypeSchema>;

const CATEGORY = {
  UN_CATEGORIZED: 'formCode_category_00',
  NON_SPECIALIZED: 'divCode_00',
} as const;

const TYPE = {
  FORM: 'formCode_type_01',
  IMAGE: 'formCode_type_02',
  VIDEO: 'formCode_type_03',
} as const;

export const SEMATIC_CATEGORY = {
  ...CATEGORY,
  ...TYPE,
} as const;

export type Type = FormCodeType;

// Keep recursive schema - generated schema has children: z.array(z.unknown()) which doesn't support recursion
const baseCategorySchema = z.object({
  id: z.string(),
  codeType: categoryTypeSchema,
  code: z.string(),
  displayName: categoryDisplaySchema.or(z.string()),
  parentId: z.string().nullable(),
});

export type CategoryData = z.infer<typeof baseCategorySchema> & {
  children?: CategoryData[];
};
const categorySchema: z.ZodType<CategoryData> = z.lazy(() =>
  baseCategorySchema.extend({
    children: z.array(categorySchema).optional(),
  }),
);

const categoriesSchema = z.array(categorySchema);

type CategoriesData = z.infer<typeof categoriesSchema>;

const queryFn = async () => {
  const { data: resp } = await smartFormApi.get<Response>(PATH);

  const result = categoriesSchema.safeParse(resp.data);

  if (result.error) {
    throw new Error(result.error.message);
  }

  return result.data;
};

export const getQueryCategoriesOptions = (
  options?: QueryOptions<CategoriesData>,
) =>
  queryOptions({
    queryKey: [PATH] as const,
    queryFn,
    ...options,
  });

export const useQueryCategories = (options?: QueryOptions<CategoriesData>) => {
  const queryOptions = getQueryCategoriesOptions(options);

  return useQuery(queryOptions);
};

export const filterVisibleCategories = (
  categories: CategoriesData,
  visibleCategoryCodes?: readonly string[],
) =>
  visibleCategoryCodes
    ? categories.filter(
        ({ codeType, code }) =>
          codeType !== 'CATEGORY' || visibleCategoryCodes.includes(code),
      )
    : categories;

const findTypeIdHelper = (kind: Type) => (category: CategoriesData[number]) =>
  category.codeType === 'TYPE' && category.displayName === kind;

export const useSuspenseQueryTypeId = (
  type: Type,
  options?: QueryOptions<CategoriesData>,
) => {
  const { data } = useSuspenseQuery(getQueryCategoriesOptions(options));
  const result = data.find(findTypeIdHelper(type));

  if (!result) {
    throw new Error(`System must have ${type} category`);
  }

  return result.id;
};

export const getTypeId = async (queryClient: QueryClient, type: Type) => {
  const categories = await queryClient.ensureQueryData(
    getQueryCategoriesOptions(),
  );
  const result = categories.find(findTypeIdHelper(type));

  if (!result) {
    throw new Error(`System must have ${type} category`);
  }

  return result.id;
};
